/**
 * 信息节点配置面板
 *
 * 配置 LLM 提取提示词和提取字段
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import type { InfoNodeData, InfoExtractField } from '../../../types/workflow';
import { useWorkflowStore } from '../../../store/workflowStore';

export interface InfoNodeConfigPanelProps {
  /** 节点 ID */
  nodeId: string;
}

export const InfoNodeConfigPanel: React.FC<InfoNodeConfigPanelProps> = React.memo(({
  nodeId,
}) => {
  // 从 store 获取节点数据
  const node = useWorkflowStore((state) =>
    state.nodes.find((n) => n.id === nodeId)
  );
  const updateNodeForm = useWorkflowStore((state) => state.updateNodeForm);

  // 本地表单状态
  const [formData, setFormData] = useState<{
    label: string;
    prompt: string;
    fields: InfoExtractField[];
  }>({
    label: '',
    prompt: '',
    fields: [],
  });

  // 错误状态
  const [errors, setErrors] = useState<{
    prompt?: string;
    fields?: string;
  }>({});

  // 同步节点数据到本地状态
  useEffect(() => {
    if (node) {
      const data = node.data as InfoNodeData;
      setFormData({
        label: data.label || '信息提取',
        prompt: data.prompt || '',
        fields: data.fields || [],
      });
    }
  }, [node]);

  // 验证表单
  const validateForm = useCallback(() => {
    const newErrors: typeof errors = {};

    if (!formData.prompt.trim()) {
      newErrors.prompt = '提取提示词不能为空';
    }

    if (formData.fields.length === 0) {
      newErrors.fields = '至少需要定义一个提取字段';
    }

    // 检查字段名是否为空或重复
    const fieldNames = formData.fields.map((f) => f.name.trim()).filter(Boolean);
    const uniqueNames = new Set(fieldNames);
    if (fieldNames.length !== uniqueNames.size) {
      newErrors.fields = '字段名不能重复';
    }

    if (formData.fields.some((f) => !f.name.trim())) {
      newErrors.fields = '字段名不能为空';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // 保存配置
  const handleSave = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    updateNodeForm(nodeId, {
      label: formData.label,
      prompt: formData.prompt.trim(),
      fields: formData.fields,
    });
  }, [nodeId, formData, updateNodeForm, validateForm]);

  // 更新字段
  const updateFormData = useCallback(<K extends keyof typeof formData>(
    field: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // 清除对应错误
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof typeof errors];
        return next;
      });
    }
  }, [errors]);

  // 添加提取字段
  const addField = useCallback(() => {
    const newField: InfoExtractField = {
      name: `field_${formData.fields.length + 1}`,
      description: '',
      required: false,
    };
    updateFormData('fields', [...formData.fields, newField]);
  }, [formData.fields, updateFormData]);

  // 删除提取字段
  const removeField = useCallback((index: number) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    updateFormData('fields', newFields);
  }, [formData.fields, updateFormData]);

  // 更新提取字段
  const updateField = useCallback((index: number, updates: Partial<InfoExtractField>) => {
    const newFields = formData.fields.map((f, i) =>
      i === index ? { ...f, ...updates } : f
    );
    updateFormData('fields', newFields);
  }, [formData.fields, updateFormData]);

  return (
    <div className="p-4 space-y-4">
      {/* 节点名称 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          节点名称
        </label>
        <input
          type="text"
          value={formData.label}
          onChange={(e) => updateFormData('label', e.target.value)}
          placeholder="信息提取"
          className={clsx(
            'w-full px-3 py-2 rounded-md border border-gray-200',
            'focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent',
            'text-sm text-gray-700',
          )}
        />
      </div>

      {/* LLM 提取提示词 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          提取提示词 <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.prompt}
          onChange={(e) => updateFormData('prompt', e.target.value)}
          onBlur={validateForm}
          placeholder="描述需要从上游节点输出中提取的信息..."
          rows={4}
          className={clsx(
            'w-full px-3 py-2 rounded-md border resize-none',
            'focus:outline-none focus:ring-2 focus:border-transparent',
            'text-sm text-gray-700 placeholder:text-gray-400',
            errors.prompt
              ? 'border-red-300 focus:ring-red-500'
              : 'border-gray-200 focus:ring-cyan-500',
          )}
        />
        {errors.prompt && (
          <p className="mt-1 text-sm text-red-500">{errors.prompt}</p>
        )}
        <p className="mt-1.5 text-xs text-gray-400">
          LLM 会根据此提示词从上游节点的输出中提取结构化信息
        </p>
      </div>

      {/* 提取字段定义 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            提取字段 <span className="text-red-500">*</span>
          </label>
          <button
            onClick={addField}
            className={clsx(
              'flex items-center gap-1 px-2 py-1 text-xs font-medium',
              'text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded',
              'transition-colors',
            )}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            添加字段
          </button>
        </div>

        {errors.fields && (
          <p className="mb-2 text-sm text-red-500">{errors.fields}</p>
        )}

        <div className="space-y-2 max-h-48 overflow-y-auto">
          {formData.fields.length === 0 ? (
            <div className="text-sm text-gray-400 italic py-4 text-center border border-dashed border-gray-200 rounded-md">
              点击"添加字段"定义提取字段
            </div>
          ) : (
            formData.fields.map((field, index) => (
              <div
                key={index}
                className="p-2 border border-cyan-200 bg-cyan-50 rounded-md"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  {/* 字段名 */}
                  <input
                    type="text"
                    value={field.name}
                    onChange={(e) => updateField(index, { name: e.target.value })}
                    placeholder="字段名"
                    className={clsx(
                      'flex-1 px-2 py-1 rounded border text-sm font-mono',
                      'border-cyan-200 focus:outline-none focus:ring-1 focus:ring-cyan-400 focus:border-transparent',
                    )}
                  />
                  {/* 必填开关 */}
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(index, { required: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-red-500 focus:ring-red-500"
                    />
                    <span className="text-xs text-gray-500">必填</span>
                  </label>
                  {/* 删除按钮 */}
                  <button
                    onClick={() => removeField(index)}
                    className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                    title="删除"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {/* 字段描述 */}
                <input
                  type="text"
                  value={field.description}
                  onChange={(e) => updateField(index, { description: e.target.value })}
                  placeholder="字段描述（可选）"
                  className={clsx(
                    'w-full px-2 py-1 rounded border text-xs',
                    'border-cyan-200 focus:outline-none focus:ring-1 focus:ring-cyan-400 focus:border-transparent',
                    'placeholder:text-gray-400',
                  )}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {/* 变量引用语法提示 */}
      <div className="p-3 bg-cyan-50 rounded-md">
        <div className="flex items-center gap-2 text-sm font-medium text-cyan-700 mb-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          引用信息节点数据
        </div>
        <div className="text-xs text-gray-500 space-y-1">
          <p>提取的数据存储在临时内存中，可在后续节点中引用：</p>
          <div className="mt-2 p-2 bg-white rounded border border-cyan-100">
            <code className="text-xs text-cyan-600">
              {`{info.${formData.label || '信息提取'}.字段名}`}
            </code>
          </div>
          <p className="text-gray-400 mt-1">
            例如：{'{info.角色提取.姓名}'}、{'{info.角色提取.年龄}'}
          </p>
        </div>
      </div>

      {/* 保存按钮 */}
      <button
        onClick={handleSave}
        className={clsx(
          'w-full py-2 px-4 rounded-md text-sm font-medium',
          'bg-cyan-500 text-white',
          'hover:bg-cyan-600 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2',
        )}
      >
        保存配置
      </button>
    </div>
  );
});

InfoNodeConfigPanel.displayName = 'InfoNodeConfigPanel';

export default InfoNodeConfigPanel;
